import React from 'react';

import { StyleSheet } from 'react-native';

import { CmsQuestRequirement } from 'components/quests/cms-hooks/types';
import { IQuest } from 'components/quests/loyalty-quest/types';
import { Tier } from 'components/quests/loyalty-quest/utils';
import { Box } from 'components/ui';
import { LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { ILoyaltyQuestsUiDetailFragment } from 'generated/sanity-graphql';
import { primitive, spacing } from 'styles/constants';

import { EarnCrownsBadge } from '../earn-crowns-badge';
import { QuestStep } from '../quest-step';

interface ITierCardProps {
  tier: Tier;
  quest: IQuest;
  // @TODO: Add proper type for cmsSteps. Expected to be 1:1 with `tier.steps`;
  // when an entry is missing/undefined, `QuestStep` falls back to data derived
  // from `cmsQuestRequirementById`.
  cmsSteps: any[];
  cmsQuestRequirementById: CmsQuestRequirement;
  cmsQuestDetailUI: ILoyaltyQuestsUiDetailFragment;
  onCloseDrawer: () => void;
  isFirstTier?: boolean;
  // When `onToggle` is omitted the badge hides its chevron and the card
  // always renders expanded — used by single-tier (regular) quests.
  isCollapsed?: boolean;
  onToggle?: () => void;
  // Renders all child `QuestStep`s in the locked-preview state. Locking is a
  // tier-context decision (it depends on the position of the first uncompleted
  // tier), so the orchestrator owns it and the card just reflects it.
  areStepsLocked?: boolean;
}

export const TierCard = ({
  tier,
  quest,
  cmsSteps,
  cmsQuestRequirementById,
  cmsQuestDetailUI,
  onCloseDrawer,
  isFirstTier,
  isCollapsed = false,
  onToggle,
  areStepsLocked = false,
}: ITierCardProps) => {
  const isTierCompleted = tier.status === LoyaltyQuestStepStatus.COMPLETED;

  return (
    <Box style={styles.container}>
      <EarnCrownsBadge
        bonusPoints={tier?.bonusPoints}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
        isFirstTier={isFirstTier}
        isTierCompleted={isTierCompleted}
      />
      {!isCollapsed &&
        (tier?.steps ?? []).map((step, stepIndex) => (
          <QuestStep
            key={step.id || stepIndex}
            step={step}
            quest={quest}
            cmsStep={cmsSteps[stepIndex]}
            cmsQuestRequirementById={cmsQuestRequirementById}
            cmsQuestDetailUI={cmsQuestDetailUI}
            onCloseDrawer={onCloseDrawer}
            isLocked={areStepsLocked}
          />
        ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: primitive.$blackOpacity10,
    borderRadius: 8,
    overflow: 'hidden',
    padding: spacing.$4,
    gap: spacing.$4,
  },
});
